import { Plus } from "lucide-react";
import { AppointmentListCard } from "@/features/appointments/components/AppointmentListCard";
import { AppointmentsList } from "@/features/appointments/components/AppointmentsList";
import type { Appointment } from "@/features/appointments/types/appointmentTypes";
import { Button } from "@/shared/components/shadcn/Button";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { ItemList } from "@/shared/components/ui/ItemList";
import { RowSkeleton } from "@/shared/components/ui/RowSkeleton";
import { Section } from "@/shared/components/ui/Section";
import { formatBusinessTime } from "@/shared/utils/formatters";
import type { CalendarEvent } from "../types/calendarEventTypes";
import { createAgenda } from "../utils/agenda";
import { CalendarEventCard } from "./CalendarEventCard";

interface CalendarAgendaProps {
  appointments: Appointment[];
  events: CalendarEvent[];
  dateKey: string;
  timezone: string;
  onAppointmentClick: (appointment: Appointment) => void;
  onEventClick: (event: CalendarEvent) => void;
  onGapAdd?: (startAt: Date) => void;
  showGaps?: boolean;
  appointmentsLoading?: boolean;
  eventsLoading?: boolean;
  appointmentsError?: Error | null;
  eventsError?: Error | null;
  onRetryAppointments?: () => void;
  onRetryEvents?: () => void;
}

export function CalendarAgenda({
  appointments, events, dateKey, timezone, onAppointmentClick, onEventClick, onGapAdd, showGaps = true,
  appointmentsLoading, eventsLoading, appointmentsError, eventsError, onRetryAppointments, onRetryEvents,
}: CalendarAgendaProps) {
  const sourcesReady = !appointmentsLoading && !eventsLoading && !appointmentsError && !eventsError;
  const agenda = createAgenda(appointments, events, dateKey, timezone, showGaps && sourcesReady);
  const hasEvents = agenda.allDay.length > 0 || agenda.timed.some((item) => item.kind === "event");
  return (
    <div className="space-y-3">
      {appointmentsError && <ErrorState message={`Appointments unavailable: ${appointmentsError.message}`} onRetry={onRetryAppointments} />}
      {eventsError && <ErrorState message={`Calendar events unavailable: ${eventsError.message}`} onRetry={onRetryEvents} />}
      {appointmentsLoading && <div aria-label="Loading appointments"><RowSkeleton /></div>}
      {eventsLoading && <div aria-label="Loading calendar events"><RowSkeleton /></div>}
      {agenda.allDay.length > 0 && <Section title="All day">
        <div className="space-y-2">{agenda.allDay.map((event) => <CalendarEventCard key={event.id} event={event} dateKey={dateKey} timezone={timezone} onClick={onEventClick} />)}</div>
      </Section>}
      {!hasEvents && appointments.length > 0
        ? <AppointmentsList appointments={appointments} onAppointmentClick={onAppointmentClick} onGapAdd={onGapAdd} showGaps={showGaps && sourcesReady} />
        : agenda.timed.length > 0 && <ItemList
          items={agenda.timed}
          renderItem={(item) => {
            if (item.kind === "appointment") return <AppointmentListCard appointment={item.appointment} onClick={onAppointmentClick} />;
            if (item.kind === "event") return <CalendarEventCard event={item.event} dateKey={dateKey} timezone={timezone} onClick={onEventClick} />;
            const minutes = Math.floor((item.endAt.getTime() - item.startAt.getTime()) / 60_000);
            return <div className="flex min-h-12 items-center gap-2 rounded-xl px-3 text-xs" style={{ backgroundImage: "repeating-linear-gradient(45deg,transparent,transparent 4px,var(--muted) 4px,var(--muted) 8px)" }}>
              <span className="rounded-lg bg-card p-2 font-semibold">{formatBusinessTime(item.startAt, timezone)}</span>
              <span className="rounded bg-card px-1.5 py-1 text-primary">{minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)}h${minutes % 60 ? ` ${minutes % 60}m` : ""}`}</span>
              {onGapAdd && <Button variant="quiet" className="ml-auto" aria-label="Add appointment in this gap" onClick={() => onGapAdd(item.startAt)}><Plus aria-hidden="true" />Add</Button>}
            </div>;
          }}
        />}
      {sourcesReady && !hasEvents && appointments.length === 0 && <EmptyState message="No appointments or calendar events for this day." />}
    </div>
  );
}
